/**
 * LSEG's Universe and UniverseParams grammars for RDP.Analytics (FinancialContracts), ported
 * from Workspace's Excel add-in (Refinitiv for Office 26.30.2) as one hand-written lexer and
 * recursive-descent parser.
 *
 * LSEG's are ANTLR grammars (UniverseLexer/Parser, UniverseParamsLexer/Parser). Their rules were
 * read from the generated classes (rule and context names, lexer predicates and actions, parser
 * methods) and their serialized ATNs, decoded offline; the lexer modes, character sets and the
 * flags that tell a value from a key below are LSEG's.
 *
 * Errors: LSEG's ANTLR error listeners only log (getModifiedErrorLiteners, 58746) and its
 * parsers recover silently; only an exception in its visitors surfaces, as
 * `Bad formula syntax: parser (antlr) <msg>!`. We reproduce the recoveries the goldens and
 * common input need (a missing closer is inserted at the end or before a token that may follow
 * it, one stray token before an expected one is deleted, `()` reads as an empty object, an
 * unknown character is dropped); any other syntax error throws that wrapper [A].
 */

/** An error whose message is LSEG's full text. */
export class AnalyticsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AnalyticsError";
  }
}

/** One instrument of the request's universe, or the global params. */
export type UniverseItem = {
  instrumentDefinition?: Record<string, unknown>;
  pricingParameters?: Record<string, unknown>;
  marketData?: Record<string, unknown>;
  [root: string]: unknown;
};

export interface UniverseCtx {
  /** The SubType argument; LSEG tags every Universe item with it as `instrumentType`. */
  subType: string;
  /** Where a bare instrument code goes in `instrumentDefinition` (BaseFCProcessor's `instrumentCode`). */
  instrumentCodeField: string;
  /** LSEG's getParamType: the section of a camelCase key, or null (then `instrumentDefinition`). */
  paramType(key: string): string | null;
  /** LSEG's isRootKey: is the key one of the subtype's paramTypes sections? Absent: none is. */
  isRootKey?(key: string): boolean;
  /**
   * Where a key with no section goes: absent, `instrumentDefinition` (BaseFCProcessor); null,
   * the item itself (BaseProcessor.getParamDetails, as DatesAndCalendars uses it).
   */
  defaultSection?: string | null;
  /** The processor's own check of a key and its value (DatesAndCalendars: `period`); it may throw. */
  checkParam?(key: string, value: unknown): void;
  /** What a bare code in Universe gives (LSEG's getInstrCodeObj); absent: `{instrumentDefinition: {[instrumentCodeField]: code}}`. */
  codeItem?(code: string): UniverseItem;
  /** false: items get no `instrumentType` (UniverseVisitorImpl.visitUniverseParam skips Type curves). */
  tagInstrumentType?: boolean;
  /** The tag each item gets instead of `instrumentType` (VolatilitySurface: `underlyingType: Capitalize(subtype)`). */
  typeTag?: { key: string; value: string };
  /** The processor's own rewrite of a key's value (VolatilitySurface: `dataPoints` values as text). */
  paramValue?(key: string, value: unknown): unknown;
}

const SEP = ";"; // LSEG: ProcessorsConfig.defaultSeparator (27821)
const CODE_WITH_PARAMS =
  "Universe instrument code with paramerters syntax / INSTR_CODE(key1:val1 key2:val2) / is not allowed.";
const antlr = (msg: string) => new AnalyticsError(`Bad formula syntax: parser (antlr) ${msg}!`); // LSEG: AntlrParserError (97368), QPSService (36844)
const removeLineBreaks = (s: string | null | undefined) => (s ? s.replace(/(\r\n|\n|\r)/gm, "") : ""); // LSEG: removeLineBreaks (44181)

// ---------------------------------------------------------------------------------------------
// Universe checks, run before the parsers

/**
 * The separator check LSEG runs on the raw Universe before anything else: brackets must pair,
 * no `,` or `|`, and no quote at all (a quote opens a "quoted" state that never closes).
 */
// LSEG: QPSUtils.validateSeparator / checkPayloadSeparator (62066), checkSeparator (5545)
export function validateUniverseSeparator(universe: string | null | undefined, separator = SEP): void {
  const fail = checkSeparator(separator, universe ?? "");
  if (fail) {
    const [msg, pos, ch] = fail;
    throw new AnalyticsError(msg + (ch ? ` "${ch}"` : "") + (pos ? ` at position ${pos}` : ""));
  }
}

type Paren = { symbol: string; couldOmit: boolean; count: number };

// LSEG: checkSeparator, getParensStack, ParensStack (5545)
function checkSeparator(sep: string, text: string): [string, number, string] | null {
  const stack: Paren[] = [];
  const open = (symbol: string, couldOmit: boolean) => {
    const p: Paren = { symbol, couldOmit, count: 1 };
    const top = stack.pop();
    if (top && top.symbol === symbol) p.count = top.count + 1;
    else if (top) stack.push(top);
    stack.push(p);
  };
  const close = (symbol: string, isSep: boolean): boolean => {
    const top = stack.pop();
    if (!top) return isSep;
    if (top.symbol === symbol) {
      if (--top.count) stack.push(top);
      return true;
    }
    if (isSep) {
      stack.push(top);
      return true;
    }
    return top.couldOmit && close(symbol, isSep);
  };
  let inQuotes = false;
  let pos = 0;
  for (const c of text) {
    pos++;
    inQuotes = c === "'" || inQuotes;
    if (inQuotes) continue;
    if (c === "(" || c === "[") open(c === "(" ? ")" : "]", c === sep);
    else if (c === ")" || c === "]") {
      if (!close(c, c === sep)) return ["Inconsistent parentheses", pos, c];
    } else if (c !== sep && /[;,|]/.test(c)) return ["Incorrect list separator symbol has been used", pos, c];
  }
  if (sep === "") return ["Separator symbol is not set", 0, ""];
  const closeAll = () => {
    const top = stack.pop();
    return !top || (top.couldOmit && stack.length === 0);
  };
  return inQuotes || !closeAll() ? ["Inconsistent parentheses and/or quotes", 0, ""] : null;
}

const CELL_REF = /^(\$?[a-zA-Z]{1,8}\$?\d{1,8})(:(\$?[a-zA-Z]+\$?\d+))?$/; // LSEG: ConverterUtil.cellRefRegex (1408)

/**
 * The Universe text LSEG parses: line breaks dropped, `()` when empty, and `key:value` text
 * not already in `( … )` wrapped in one group.
 */
// LSEG: BaseFCProcessor.getUniverseArg (41170), BaseProcessor.verifyBrackets / validateUniverseField (27957)
export function getUniverseArg(universe: string | null | undefined): string {
  const text = removeLineBreaks(universe);
  if (!text) return "()";
  if (CELL_REF.test(text) || !text.includes(":")) return text;
  const t = text.trim();
  let i = 0;
  while (i < t.length && t[i] !== ":" && t[i] !== "(") i++;
  return t[i] === "(" && t[t.length - 1] === ")" ? text : `(${text})`;
}

/** Refuses an instrument code directly followed by `(`, e.g. `FR10YT=RR(ValuationDate:…)`. */
// LSEG: BaseFCProcessor.checkIfHasInstrumentCodeWithParamsSyntax (41170)
export function checkInstrumentCodeWithParams(universe: string): void {
  for (let i = 0; i < universe.length; ) {
    let j = i;
    while (j < universe.length && /[\w=.\-@]/.test(universe[j])) j++;
    if (j > i) {
      if (universe[j] === "(") throw new AnalyticsError(CODE_WITH_PARAMS);
      i = j;
    } else i++;
  }
}

// ---------------------------------------------------------------------------------------------
// Lexer: LSEG's modes, character sets, predicates and actions (UniverseLexer 38781 via 62477,
// UniverseParamsLexer 73919 via 83631, BaseLexerImpl 87987)

const enum T {
  EOF, Code, GroupSep, LGroup, Value, Key, Delim, QOpen, LArr, LObj, RObj, RArr, RGroup, ItemSep, ParamSep, QText, QClose, Start,
}
type Tok = { t: T; text: string };
type Mode = "default" | "instr" | "array" | "quote"; // LSEG's DEFAULT, INSTR_PARAMS, ARRAY_VALUE, WHITESPACE_PARAM_VALUE
type Rule = [len: (i: number) => number, t: T | null, act?: (next: string | undefined) => void];

const CODE_CH = /[-.0-9=@-Z_a-z]/; // INSTRCODE
const KEY_CH = /[-.0-9@-Za-z]/; // PLAIN_PARAM_KEY
const PLAIN_CH = /[!#-&*+\-./0-9:<-Z^-~]/; // PLAIN_PARAM_CHAR, besides the separator
const WS_CH = /[\t\n\r ]/;

function tokenize(input: string, grammar: "universe" | "params"): Tok[] {
  const modes: Mode[] = ["default"];
  let plain = false; // isPlainParam: may the next run be a value rather than a key?
  let plainArr = false; // isPlainArrParam
  const isPlain = (c: string | undefined) => c !== "(" && c !== "[" && c !== "'"; // getIsPlainParam
  const run = (ok: (c: string) => boolean) => (i: number) => {
    let j = i;
    while (j < input.length && ok(input[j])) j++;
    return j - i;
  };
  const lit = (s: string) => (i: number) => (input.startsWith(s, i) ? s.length : 0);
  const value = (i: number) => (plain || plainArr ? run((c) => PLAIN_CH.test(c) && c !== SEP)(i) : 0);
  const key = run((c) => KEY_CH.test(c));
  const delim = (i: number) => (input[i] === ":" || input[i] === "=" ? 1 : 0);
  const ws = (i: number) => (WS_CH.test(input[i] ?? "") ? 1 : 0);
  const pushQuote = () => void modes.push("quote");
  const pushArray = (n: string | undefined) => {
    plainArr = isPlain(n);
    modes.push("array");
  };
  const instr: Rule[] = [
    [value, T.Value, (n) => void (n !== SEP && (plain = false))],
    [key, T.Key],
    [delim, T.Delim, (n) => void (plain = isPlain(n))],
    [lit("'"), T.QOpen, pushQuote],
    [lit("["), T.LArr, pushArray],
    [lit("("), T.LObj, () => void (plain = false)],
    [lit(")"), T.RObj],
    [lit("]"), T.RArr, () => void (plainArr = false)],
    [ws, null],
  ];
  const tables: Record<Mode, Rule[]> = {
    default:
      grammar === "universe"
        ? [[run((c) => CODE_CH.test(c)), T.Code], [lit(SEP), T.GroupSep], [lit("("), T.LGroup, () => void modes.push("instr")]]
        : [[lit("START"), T.Start, () => void modes.push("instr")]],
    instr:
      grammar === "universe"
        ? [...instr, [lit(")" + SEP), T.RGroup, () => void modes.pop()]]
        : [[lit(SEP), T.ParamSep, (n) => void (n === " " && (plain = false))], ...instr],
    array: [
      [lit("'"), T.QOpen, pushQuote],
      [value, T.Value, () => void (plain = false)],
      [key, T.Key],
      [lit("("), T.LObj, () => void (plain = false)],
      [lit(")"), T.RObj],
      [delim, T.Delim, (n) => void (plain = isPlain(n))],
      [lit(SEP), T.ItemSep],
      [ws, null],
      [lit("["), T.LArr, pushArray],
      [lit("]"), T.RArr, () => void ((plainArr = false), modes.pop())],
    ],
    quote: [[run((c) => c !== "'"), T.QText], [lit("'"), T.QClose, () => void modes.pop()]],
  };
  const toks: Tok[] = [];
  for (let i = 0; i < input.length; ) {
    let best = 0;
    let rule: Rule | null = null;
    for (const r of tables[modes[modes.length - 1]]) {
      const n = r[0](i);
      if (n > best) [best, rule] = [n, r]; // longest match, first rule on a tie
    }
    if (!rule) {
      i++; // ANTLR's lexer recovery: the unknown character is reported and dropped
      continue;
    }
    const text = input.slice(i, (i += best));
    rule[2]?.(input[i]);
    if (rule[1] !== null) toks.push({ t: rule[1], text });
  }
  toks.push({ t: T.EOF, text: "<EOF>" });
  return toks;
}

// ---------------------------------------------------------------------------------------------
// Parser: LSEG's rules (UniverseParser 72284, UniverseParamsParser 77066), as its generated methods
// take them; the visitors' results are built afterwards (UniverseVisitorImpl 80759,
// UniverseParamsParserVisitorImpl 21612, BaseVisitor 89223)

const VALUE_START: ReadonlySet<T> = new Set([T.Value, T.QOpen, T.LObj, T.LArr]);
const TOP_U: ReadonlySet<T> = new Set([T.Code, T.GroupSep, T.LGroup, T.EOF]);
const TOP_P: ReadonlySet<T> = new Set([T.ParamSep, T.Start, T.Key, T.LObj, T.EOF]);
const IN_ARRAY: ReadonlySet<T> = new Set([...VALUE_START, T.ItemSep, T.RArr, T.EOF]);
const inGroup = (closer: T): ReadonlySet<T> => new Set([T.Key, closer, T.EOF]);

type Obj = Record<string, unknown>;

class Parser {
  private i = 0;
  constructor(private readonly toks: Tok[]) {}
  la(k = 0): T {
    return this.toks[Math.min(this.i + k, this.toks.length - 1)].t;
  }
  next(): string {
    return this.toks[Math.min(this.i++, this.toks.length - 1)].text;
  }
  /** The first token after the run of ParamSep starting here. */
  afterSeps(): T {
    let k = 0;
    while (this.la(k) === T.ParamSep) k++;
    return this.la(k);
  }
  fail(): never {
    const tok = this.toks[Math.min(this.i, this.toks.length - 1)];
    throw antlr(`mismatched input '${tok.text}'`);
  }
  /** ANTLR's match with its inline recovery: delete one stray token, or insert the expected one. */
  match(t: T, follow: ReadonlySet<T>): string {
    if (this.la() === t) return this.next();
    if (this.la(1) === t) {
      this.i++;
      return this.next();
    }
    if (follow.has(this.la())) return "";
    return this.fail();
  }
  /** instrParam: KEY DELIM paramValue, the key camel-cased (BaseVisitor.visitInstrParam). */
  instrParams(into: Obj, follow: ReadonlySet<T>): Obj {
    while (this.la() === T.Key) {
      const key = camel(this.next());
      this.match(T.Delim, VALUE_START);
      into[key] = this.value(follow);
    }
    return into;
  }
  value(follow: ReadonlySet<T>): unknown {
    switch (this.la()) {
      case T.Value:
        return typed(this.next());
      case T.QOpen: {
        this.next();
        const text = this.la() === T.QText ? this.next() : ""; // whitespaceparam / emptyparam
        this.match(T.QClose, follow);
        return text;
      }
      case T.LObj:
        return this.object(follow, false);
      case T.LArr: {
        this.next();
        const items: unknown[] = [];
        while (VALUE_START.has(this.la())) items.push(this.value(IN_ARRAY));
        while (this.la() === T.ItemSep) {
          this.next();
          items.push(this.value(IN_ARRAY));
        }
        this.match(T.RArr, follow);
        return items;
      }
      default:
        return this.fail();
    }
  }
  /**
   * objectParamValue: ( instrParam+ ). `()` recovers to {}. At group level a missing `(` before
   * a key is inserted, and anything else is skipped up to what may follow a group (ANTLR's recover).
   */
  object(follow: ReadonlySet<T>, groupLevel: boolean): Obj {
    if (this.la() !== T.LObj && this.la(1) === T.LObj) this.i++;
    if (this.la() === T.LObj) this.next();
    else if (groupLevel && this.la() !== T.Key) {
      while (!TOP_P.has(this.la())) this.i++;
      return {};
    } else if (!groupLevel) this.fail();
    if (this.la() === T.RObj) {
      this.next();
      return {};
    }
    if (this.la() !== T.Key) this.fail();
    const obj = this.instrParams({}, inGroup(T.RObj));
    this.match(T.RObj, follow);
    return obj;
  }
}

const camel = (s: string) => (s ? s[0].toLowerCase() + s.slice(1) : ""); // LSEG: toCamelCase (44181)

/** A plain value's type: numbers, `true`/`false`, else the text (quoted values are not typed). */
// LSEG: BaseVisitor.makeParamValueOfCorrectType (89223)
function typed(text: string): unknown {
  const n = Number(text);
  if (n || text === "0") return n;
  if (text === "true" || text === "false") return text === "true";
  return text;
}

// LSEG: BaseVisitor.populateParamObj / populateObjectKey (89223), BaseFCProcessor.getParamDetails (41170)
function populate(target: UniverseItem, key: string, value: unknown, ctx: UniverseCtx): void {
  if (value === null || value === undefined) return;
  const k = camel(key);
  if (typeof value === "object" && ctx.isRootKey?.(k)) {
    for (const sub of Object.keys(value as Obj)) put(target, k, sub, (value as Obj)[sub]);
    return;
  }
  // A processor's check throws inside LSEG's visitor, so AntlrParser wraps it (29768).
  try {
    ctx.checkParam?.(k, value);
  } catch (e) {
    throw antlr(e instanceof Error ? e.message : String(e));
  }
  if (ctx.paramValue) value = ctx.paramValue(k, value);
  const section = ctx.paramType(k) || (ctx.defaultSection === undefined ? "instrumentDefinition" : ctx.defaultSection);
  if (section) put(target, section, k, value);
  // LSEG: BaseVisitor.populateObjectKey with no section: an object merges into one already there
  else if (Object.prototype.hasOwnProperty.call(target, k) && (value as object).constructor === Object)
    target[k] = { ...(target[k] as Obj), ...(value as Obj) };
  else target[k] = value;
}

function put(target: UniverseItem, section: string, key: string, value: unknown): void {
  if (!Object.prototype.hasOwnProperty.call(target, section)) target[section] = {};
  (target[section] as Obj)[key] = value;
}

/**
 * The Universe's instruments, from the text getUniverseArg gives (LSEG appends the separator).
 * A code gives `{instrumentDefinition: {[instrumentCodeField]: code}}`; a `( … )` group gives its
 * params, placed by `paramType`; an empty slot gives `{instrumentDefinition: {}}`. Each item then
 * gets `instrumentType: subType` when there is a subtype, so `()` (LSEG's empty Universe) gives
 * `[{instrumentType: subType}]`.
 */
// LSEG: UniverseVisitorImpl (80759); input built in BaseFCProcessor.getUniverse (41170)
export function parseUniverse(text: string, ctx: UniverseCtx): UniverseItem[] {
  const p = new Parser(tokenize(text + SEP, "universe"));
  const raw: ({ code: string } | { params: Obj } | null)[] = [];
  for (;;) {
    if (p.la() === T.Code) {
      const code = p.next();
      p.match(T.GroupSep, TOP_U);
      raw.push({ code });
    } else if (p.la() === T.LGroup) {
      p.next();
      raw.push({ params: p.instrParams({}, inGroup(T.RGroup)) });
      p.match(T.RGroup, TOP_U);
    } else if (p.la() === T.GroupSep) {
      p.next();
      raw.push(null);
    } else break; // no EOF in LSEG's rule: what follows is left unread
  }
  if (!raw.length) p.fail();
  return raw.map((r) => {
    let item: UniverseItem;
    if (r === null) item = ctx.subType ? { instrumentDefinition: {} } : {};
    else if ("code" in r) item = ctx.codeItem ? { ...ctx.codeItem(r.code) } : { instrumentDefinition: { [ctx.instrumentCodeField]: r.code } };
    else {
      item = {};
      for (const k of Object.keys(r.params)) {
        if ((ctx.paramType(k) || "instrumentDefinition") === "pricingParameters")
          throw antlr(`Found pricingParameters field \`${k}\` in universe. Param should be in universeParams.`);
        populate(item, k, r.params[k], ctx);
      }
    }
    if (ctx.subType && ctx.typeTag) item[ctx.typeTag.key] = ctx.typeTag.value;
    else if (ctx.subType && ctx.tagInstrumentType !== false) item.instrumentType = ctx.subType;
    return item;
  });
}

/**
 * UniverseParams: `key:value` pairs are global, `key:a;b` gives one value per instrument, and
 * `( … );( … )` groups are per instrument. `perInstrument` is LSEG's `universes` array as is:
 * an instrument that got nothing (a `()` group) is a hole, and a null value (`key:a;`) still
 * adds an empty item. `global` keeps its pricingParameters/marketData; the caller lifts them.
 */
// LSEG: UniverseParamsParserVisitorImpl (21612); input "START" + text, BaseFCProcessor.getUniverse (41170)
export function parseUniverseParams(text: string, ctx: UniverseCtx): { global: UniverseItem; perInstrument: UniverseItem[] } {
  const p = new Parser(tokenize("START" + removeLineBreaks(text), "params"));
  const global: UniverseItem = {};
  const universes: UniverseItem[] = [];
  const at = (n: number) => universes[n] || (universes[n] = {});
  for (;;) {
    const la = p.la();
    if (la === T.Start) p.next();
    else if (la === T.Key) {
      // universeInstrParam: KEY DELIM nullParamValue* paramValue (SEP nullParamValue* paramValue)* nullParamValue*
      const key = camel(p.next());
      p.match(T.Delim, new Set([...VALUE_START, T.ParamSep]));
      const values: unknown[] = [];
      while (p.la() === T.ParamSep) p.next(), values.push(null);
      values.push(p.value(TOP_P));
      while (p.la() === T.ParamSep) {
        const more = VALUE_START.has(p.afterSeps());
        if (more) p.next();
        while (p.la() === T.ParamSep) p.next(), values.push(null);
        if (!more) break;
        values.push(p.value(TOP_P));
      }
      if (values.length === 1) populate(global, key, values[0], ctx);
      else values.forEach((v, n) => populate(at(n), key, v, ctx));
    } else if (la === T.LObj || la === T.ParamSep) {
      // groupParam: nullGroupParamValue* objectParamValue (SEP nullGroupParamValue* objectParamValue)*
      const groups: Obj[] = [];
      while (p.la() === T.ParamSep) p.next(), groups.push({});
      groups.push(p.object(TOP_P, true));
      while (p.la() === T.ParamSep && p.afterSeps() === T.LObj) {
        p.next();
        while (p.la() === T.ParamSep) p.next(), groups.push({});
        groups.push(p.object(TOP_P, true));
      }
      groups.forEach((g, n) => Object.keys(g).forEach((k) => populate(at(n), k, g[k], ctx)));
    } else break; // no EOF in LSEG's rule: what follows is left unread
  }
  return { global, perInstrument: universes };
}
